import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { Blocker, BlockerFunction, useBlocker } from 'react-router-dom';

type BlockerEntry = {
  shouldBlockFn: BlockerFunction;
  handler: (blocker: Blocker) => void;
};

type NavigationBlockContextValue = {
  addBlocker: (
    key: string,
    shouldBlockFn: BlockerFunction,
    handler: (blocker: Blocker) => void
  ) => void;
  removeBlocker: (key: string) => void;
};

const NavigationBlockContext = createContext<NavigationBlockContextValue>({
  addBlocker: () => {},
  removeBlocker: () => {}
});

const NavigationBlockContextProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const blockersRef = useRef<Map<string, BlockerEntry>>(new Map());
  const capturedKeysRef = useRef<string[]>([]);
  const [blockerCount, setBlockerCount] = useState(0);

  const blocker = useBlocker(
    useCallback(
      args => {
        const matched: string[] = [];
        blockersRef.current.forEach((entry, key) => {
          if (entry.shouldBlockFn(args)) {
            matched.push(key);
          }
        });
        capturedKeysRef.current = matched;
        return matched.length > 0;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [blockerCount]
    )
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const keys = capturedKeysRef.current;
      capturedKeysRef.current = [];
      keys.forEach(key => {
        const entry = blockersRef.current.get(key);
        entry?.handler(blocker);
      });
    }
  }, [blocker]);

  const addBlocker = useCallback(
    (key: string, shouldBlockFn: BlockerFunction, handler: (blocker: Blocker) => void) => {
      const isNewKey = !blockersRef.current.has(key);
      blockersRef.current.set(key, { shouldBlockFn, handler });
      if (isNewKey) {
        setBlockerCount(c => c + 1);
      }
    },
    []
  );

  const removeBlocker = useCallback((key: string) => {
    if (!blockersRef.current.has(key)) return;
    blockersRef.current.delete(key);
    setBlockerCount(c => c - 1);
  }, []);

  return (
    <NavigationBlockContext.Provider value={{ addBlocker, removeBlocker }}>
      {children}
    </NavigationBlockContext.Provider>
  );
};

const useNavigationBlockContext = () => useContext(NavigationBlockContext);

export { NavigationBlockContextProvider, useNavigationBlockContext };
export type { NavigationBlockContextValue };
